import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Lightning, MapPin } from "@phosphor-icons/react";
import { HERO_IMAGE } from "@/data/services";
import { scrollTo } from "@/components/site/Header";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["WHEN THE ROAD", "STOPS YOU —", "WE MOVE FAST."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Night road rescue"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-black" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-rescue-bg via-transparent to-black/40" />
      <div className="absolute inset-0 z-10 spotlight" />

      <div className="relative z-20 mx-auto max-w-[1400px] px-5 sm:px-8 min-h-screen flex flex-col justify-end pb-16 pt-28">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-2.5 mb-6"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-rescue-primary opacity-75 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rescue-primary" />
          </span>
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-white/80">
            24 × 7 · Live dispatch · Any vehicle
          </span>
        </motion.div>

        <h1 className="font-display uppercase leading-[0.85] tracking-tighter text-white text-[16vw] sm:text-[13vw] lg:text-[9.5vw]">
          {HEADLINE.map((l, i) => (
            <span key={i} className="reveal-mask">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {l.includes("FAST") ? (
                  <>
                    WE MOVE <span className="text-rescue-primary">FAST.</span>
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="max-w-md text-base sm:text-lg text-white/70 leading-relaxed"
          >
            Puncture, dead battery, towing, servicing or instant insurance — one tap and
            help is already on the way. Round the clock. Every road.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-request-btn"
              onClick={() => scrollTo("request")}
              className="group inline-flex items-center gap-3 bg-rescue-primary hover:brightness-110 active:scale-95 transition-all text-white text-base font-semibold pl-7 pr-5 py-4 rounded-sm uppercase tracking-wide"
            >
              Request Help Now
              <span className="grid place-items-center h-7 w-7 bg-black/25 rounded-sm group-hover:translate-x-1 transition-transform">
                <ArrowRight weight="bold" size={18} />
              </span>
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => scrollTo("services")}
              className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 active:scale-95 transition-all text-white text-base font-semibold px-6 py-4 rounded-sm uppercase tracking-wide"
            >
              View Services
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10"
        >
          {[
            { icon: Lightning, k: "18 min", v: "Avg. arrival time" },
            { icon: MapPin, k: "240+", v: "Cities covered" },
            { icon: Lightning, k: "50k+", v: "Rescues completed" },
          ].map((s, i) => (
            <div key={i} className="bg-rescue-bg/60 backdrop-blur-sm px-5 py-5 flex items-center gap-3">
              <s.icon weight="fill" className="text-rescue-primary shrink-0" size={22} />
              <div>
                <div className="font-display text-3xl leading-none">{s.k}</div>
                <div className="text-xs text-white/60 mt-1">{s.v}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
